import { FormGroup, Validators } from '@angular/forms';
import { extractBankCode, extractBankName } from '../cashout-bank.helpers';

/** Propósitos permitidos por Fuse create payout (API Lorum). */
export const AED_FUSE_PAYOUT_PURPOSE_OPTIONS: {
  value: string;
  label: string;
}[] = [
  'charity',
  'commercial_investment',
  'corporate_card',
  'credit_card',
  'dividend',
  'family',
  'financial_services',
  'goods_sold',
  'goods_bought',
  'government',
  'insurance',
  'intergroup_transfer',
  'intra_group_dividends',
  'information_technology',
  'leasing',
  'loan_charges',
  'merchant_settlement',
  'mobile_wallet',
  'non_resident_transfer_between_accounts',
  'none',
  'pension',
  'personal_expenses',
  'prepaid_cards',
  'professional',
  'rental',
  'resident_transfer_between_accounts',
  'salaries',
  'telecommunications',
  'travel',
  'utility_bill',
].map((value) => ({ value, label: value }));

export function setupAedCashoutForm(ctx: any): void {
  setDefaultAedFuseBank(ctx);
  updateAedFusePayoutValidators(ctx);
}

/** AED vía subwallet FUSE_AED (external-providers / Lorum). */
export function setDefaultAedFuseBank(ctx: any): void {
  const banks = ctx.getBanks();
  if (!banks?.length) return;

  const selected =
    banks.find((b: any) => extractBankCode(b)?.toUpperCase() === 'FUSE_AED') ??
    banks[0];
  const name = extractBankName(selected);
  const code = extractBankCode(selected);
  if (code) {
    ctx.newTransactionForm.controls.company_id.setValue(selected);
    ctx.newTransactionForm.controls.company_name.setValue(
      name || selected?.shortname || selected?.name || code,
    );
  }

  const accountNumber = ctx.newTransactionForm.controls.account_number;
  accountNumber.clearValidators();
  accountNumber.updateValueAndValidity({ emitEvent: false });
}

export function setAedFuseBeneficiaryType(
  ctx: any,
  type: 'individual' | 'corporate',
): void {
  ctx.newTransactionForm
    .get('fuse_payout')
    ?.get('beneficiary_type')
    ?.setValue(type);
}

/** Validación alineada con Fuse createPayout (external-providers). */
export function updateAedFusePayoutValidators(ctx: any): void {
  const fuseGroup = ctx.newTransactionForm.get(
    'fuse_payout',
  ) as FormGroup | null;
  if (!fuseGroup) return;

  const setReq = (name: string, required: boolean) => {
    const control = fuseGroup.get(name);
    if (!control) return;
    if (required) control.setValidators([Validators.required]);
    else control.clearValidators();
    control.updateValueAndValidity({ emitEvent: false });
  };

  setReq('account_id', true);
  setReq('purpose', true);
  setReq('address_country_code', true);
  setReq('address_line_1', true);
  setReq('bank_bic', false);
  const rail = fuseGroup.get('rail')?.value;
  if (rail === 'iban') {
    setReq('iban', true);
    setReq('wire_account', false);
    setReq('wire_routing', false);
  } else {
    setReq('iban', false);
    setReq('wire_account', true);
    setReq('wire_routing', true);
  }
}

export function buildAedFusePayoutPayload(
  ctx: any,
): Record<string, unknown> | null {
  const fp = (ctx.newTransactionForm.get('fuse_payout') as FormGroup).getRawValue();
  const first = (ctx.newTransactionForm.controls.firstname.value ?? '')
    .toString()
    .trim();
  const last = (ctx.newTransactionForm.controls.lastname.value ?? '')
    .toString()
    .trim();
  const holder = `${first} ${last}`.trim() || 'Beneficiary';
  const amountRaw = ctx.newTransactionForm.controls.amount.value;
  const amountNum =
    typeof amountRaw === 'string' ? parseFloat(amountRaw) : Number(amountRaw);
  if (!Number.isFinite(amountNum) || amountNum < 0) return null;

  const accountIdentifier =
    fp.rail === 'iban'
      ? { iban: (fp.iban ?? '').toString().trim() }
      : {
          wire: {
            account_number: (fp.wire_account ?? '').toString().trim(),
            routing_number: (fp.wire_routing ?? '').toString().trim(),
          },
        };

  let externalReference = (fp.external_reference ?? '').toString().trim();
  if (!externalReference) {
    const tid = ctx.newTransactionForm.controls.transaction_id.value;
    externalReference = tid ? String(tid) : `TS-FUSE-${Date.now()}`;
  }

  return {
    account_id: (fp.account_id ?? '').toString().trim(),
    amount: Math.round(amountNum * 100),
    currency: 'AED',
    external_reference: externalReference.slice(0, 100),
    purpose: fp.purpose,
    beneficiary: {
      account_holder_name: holder,
      type: fp.beneficiary_type === 'corporate' ? 'business' : 'individual',
      account_identifier: accountIdentifier,
      address: {
        country_code: (fp.address_country_code ?? '')
          .toString()
          .trim()
          .toUpperCase(),
        line_1: (fp.address_line_1 ?? '').toString().trim(),
      },
    },
  };
}
